import { last, without } from 'lib/array/arrayUtils'

export type TripChatMobileOverlayId = 'actions' | 'cart' | 'drawer' | 'menu' | 'search'

export interface TripChatMobileOverlayState {
  cartCloseRequest: number
  actionsCloseRequest: number
  drawerCloseRequest: number
  menuCloseRequest: number
  searchCloseRequest: number
  ignoreNextPopState: boolean
  overlayHistoryDepth: number
  overlayStack: Array<TripChatMobileOverlayId>
}

export interface TripChatMobileOverlayTransition {
  shouldPushHistory: boolean
  shouldStepBackHistory: boolean
  state: TripChatMobileOverlayState
}

export const TRIP_CHAT_MOBILE_OVERLAY_HISTORY_STATE = {
  tripChatMobileOverlay: true,
} as const

export const initialTripChatMobileOverlayState: TripChatMobileOverlayState = {
  cartCloseRequest: 0,
  actionsCloseRequest: 0,
  drawerCloseRequest: 0,
  menuCloseRequest: 0,
  searchCloseRequest: 0,
  ignoreNextPopState: false,
  overlayHistoryDepth: 0,
  overlayStack: [],
}

export function isTripChatMobileOverlayHistoryState(state: unknown): boolean {
  return typeof state === 'object' &&
    state !== null &&
    'tripChatMobileOverlay' in state &&
    state.tripChatMobileOverlay === true
}

export function getTopOverlayId(stack: Array<TripChatMobileOverlayId>): TripChatMobileOverlayId | undefined {
  return last(stack)
}

function addCloseRequest(
  state: TripChatMobileOverlayState,
  overlayId: TripChatMobileOverlayId,
): TripChatMobileOverlayState {
  switch (overlayId) {
    case 'actions':
      return {
        ...state,
        actionsCloseRequest: state.actionsCloseRequest + 1,
      }
    case 'cart':
      return {
        ...state,
        cartCloseRequest: state.cartCloseRequest + 1,
      }
    case 'drawer':
      return {
        ...state,
        drawerCloseRequest: state.drawerCloseRequest + 1,
      }
    case 'menu':
      return {
        ...state,
        menuCloseRequest: state.menuCloseRequest + 1,
      }
    case 'search':
      return {
        ...state,
        searchCloseRequest: state.searchCloseRequest + 1,
      }
  }
}

export function openOverlayState(
  state: TripChatMobileOverlayState,
  overlayId: TripChatMobileOverlayId,
): TripChatMobileOverlayTransition {
  if (getTopOverlayId(state.overlayStack) === overlayId) {
    return {
      shouldPushHistory: false,
      shouldStepBackHistory: false,
      state,
    }
  }

  if (overlayId === 'search' && getTopOverlayId(state.overlayStack) === 'menu') {
    return {
      shouldPushHistory: false,
      shouldStepBackHistory: false,
      state: addCloseRequest({
        ...state,
        overlayStack: [...state.overlayStack.slice(0, -1), overlayId],
      }, 'menu'),
    }
  }

  return {
    shouldPushHistory: true,
    shouldStepBackHistory: false,
    state: {
      ...state,
      overlayHistoryDepth: state.overlayHistoryDepth + 1,
      overlayStack: [...without(state.overlayStack, overlayId), overlayId],
    },
  }
}

export function closeOverlayState(
  state: TripChatMobileOverlayState,
  overlayId: TripChatMobileOverlayId,
  options: { shouldRequestOverlayClose: boolean; shouldStepHistory: boolean },
): TripChatMobileOverlayTransition {
  if (!state.overlayStack.includes(overlayId)) {
    return {
      shouldPushHistory: false,
      shouldStepBackHistory: false,
      state,
    }
  }

  const isTopOverlay = getTopOverlayId(state.overlayStack) === overlayId
  const nextStack = isTopOverlay ? state.overlayStack.slice(0, -1) : without(state.overlayStack, overlayId)
  const shouldStepBackHistory = isTopOverlay && options.shouldStepHistory && state.overlayHistoryDepth > 0

  const nextState = {
    ...state,
    ignoreNextPopState: shouldStepBackHistory ? true : state.ignoreNextPopState,
    overlayHistoryDepth: isTopOverlay ? Math.max(0, state.overlayHistoryDepth - 1) : state.overlayHistoryDepth,
    overlayStack: nextStack,
  }

  return {
    shouldPushHistory: false,
    shouldStepBackHistory,
    state: isTopOverlay && options.shouldRequestOverlayClose ? addCloseRequest(nextState, overlayId) : nextState,
  }
}

export function closeTopOverlayFromBrowserState(
  state: TripChatMobileOverlayState,
): TripChatMobileOverlayTransition {
  const topOverlayId = getTopOverlayId(state.overlayStack)
  if (!topOverlayId) {
    return {
      shouldPushHistory: false,
      shouldStepBackHistory: false,
      state,
    }
  }

  const nextState = {
    ...state,
    overlayHistoryDepth: Math.max(0, state.overlayHistoryDepth - 1),
    overlayStack: state.overlayStack.slice(0, -1),
  }

  return {
    shouldPushHistory: false,
    shouldStepBackHistory: false,
    state: addCloseRequest(nextState, topOverlayId),
  }
}

export function consumeIgnoredPopState(state: TripChatMobileOverlayState): TripChatMobileOverlayState {
  return {
    ...state,
    ignoreNextPopState: false,
  }
}
